import { SITE_FACTS } from "@shared/siteFacts";
import { openConsentSettings } from "@/lib/consent";
import { Link, useLocation } from "wouter";
import { recordSearchClick } from "@/lib/searchTelemetry";
import { useState, useCallback } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search,
  Menu,
  X,
  User,
  LogOut,
  ChevronDown,
  Mail,
  BookOpen,
  Tag,
  LayoutGrid,
  Briefcase,
  Star,
} from "lucide-react";
import { visibleCategories, getToolById } from "@/lib/tools-data";
import { WORKFLOW_PRODUCT_IDS } from "@shared/tiers";
import { FEATURED_APP_IDS, FEATURED_APP_META } from "@/lib/featured-apps-data";
import { isPremiumApp } from "@/lib/tiers";
import { BUNDLES } from "@/lib/bundles-data";
import { useAutoResumeCheckout } from "@/hooks/useAutoResumeCheckout";
import { showUpdateBanner } from "@/hooks/useUpdateBanner";
import {
  updateSearchPanelQuery,
  useSearchPanelState,
  beginSearchPanelSearch,
  finishSearchPanelSearch,
  failSearchPanelSearch,
  cancelSearchPanelSearch,
} from "@/hooks/useSearchPanel";
import { useIsMobile } from "@/hooks/useMobile";
import { getCategoryName } from "@/lib/i18n/category-persona-translations";
import { LogoMark } from "@/components/LogoMark";
import { ThemeToggle } from "@/components/ThemeToggle";
import { personas } from "@/lib/personas-data";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { ProStatusBadge } from "@/components/ProStatusBadge";
import { CreateShortcutButton } from "@/components/CreateShortcutButton";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import { DailyLimitPaywallDialog } from "@/components/DailyLimitPaywallDialog";
import { FunnelBeacon } from "@/components/FunnelBeacon";
import { GuestCheckoutDialog } from "@/components/GuestCheckoutDialog";
import { PdfPasswordDialog } from "@/components/PdfPasswordDialog";
import { SearchSidePanel } from "@/components/SearchSidePanel";
import { type KnowledgeBaseSearchResults } from "@/lib/knowledgeBaseSearch";
import { SearchResultsDropdown } from "@/components/SearchResultsDropdown";
import { CommandPaletteTrigger } from "@/components/CommandPaletteTrigger";
import { OfflineGate } from "@/components/OfflineGate";
import { useLocale } from "@/hooks/useLocale";

// Logical groupings for the header Category dropdown -- purely a display
// grouping, doesn't change routing/SEO/category structure underneath.
// Every visible category id must appear in exactly one group.
const CATEGORY_GROUPS: { label: string; ids: string[] }[] = [
  {
    label: "Documents & Files",
    ids: [
      "pdf-tools",
      "powerpoint-slides",
      "file-tools",
      "business-tools",
      "marketing-design-tools",
      "3d-tools",
    ],
  },
  {
    label: "Calculators & Money",
    ids: [
      "converters",
      "tax-calculators",
      "finance-calculators",
      "ecommerce-tools",
      "college-tools",
    ],
  },
  {
    label: "Life & Planning",
    ids: [
      "health-calculators",
      "travel-tools",
      "event-tools",
      "household-tools",
      "relocation-tools",
    ],
  },
  {
    label: "Design & Creative",
    ids: [
      "image-tools",
      "video-tools",
      "design-tools",
      "animation-tools",
      "qr-tools",
    ],
  },
  {
    label: "Developer & Marketing",
    ids: ["text-tools", "security-tools", "seo-tools"],
  },
];

function CategoryDropdown() {
  const { locale, localizedHref, strings } = useLocale();
  const categoryMap = new Map(visibleCategories.map(c => [c.id, c]));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5 font-medium">
          <LayoutGrid className="h-4 w-4" /> {strings.nav.category}{" "}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-64 max-h-[70vh] overflow-y-auto"
      >
        {CATEGORY_GROUPS.map((group, gi) => (
          <div key={group.label}>
            {gi > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {group.label}
            </DropdownMenuLabel>
            {group.ids.map(id => {
              const cat = categoryMap.get(id);
              if (!cat) return null;
              return (
                <DropdownMenuItem key={id} asChild>
                  <Link
                    href={localizedHref(`/category/${id}`)}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <cat.icon
                      className="h-4 w-4"
                      style={{ color: cat.color }}
                    />
                    {getCategoryName(locale, cat.id, cat.name)}
                  </Link>
                </DropdownMenuItem>
              );
            })}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ForYourWorkDropdown() {
  const { localizedHref, strings } = useLocale();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5 font-medium">
          <Briefcase className="h-4 w-4" /> {strings.nav.forYourWork}{" "}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-64 max-h-[70vh] overflow-y-auto"
      >
        {personas.map(persona => (
          <DropdownMenuItem key={persona.id} asChild>
            <Link
              href={localizedHref(`/solutions/${persona.id}`)}
              className="flex items-center gap-2 cursor-pointer"
            >
              <persona.icon
                className="h-4 w-4"
                style={{ color: persona.color }}
              />
              {persona.title}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SearchBar() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<KnowledgeBaseSearchResults>({ tools: [], faq: [], personas: [], blog: [], topResult: null });
  const [isFocused, setIsFocused] = useState(false);
  const isMobile = useIsMobile();
  const [, navigate] = useLocation();

  // Search runs only when someone submits it (Enter), never while they
  // type. Search-as-you-type caused two real problems, both confirmed on
  // the live site: (1) results (especially the AI fallback, which can
  // take seconds) landing mid-keystroke wrote an older query back over
  // what was being typed, so the box fought the person typing in it;
  // and (2) it acted on half-typed text -- proposing tools and even
  // multi-step plans for "htm", "htmt", "html to" -- which is worse than
  // proposing nothing. The result's own generation is tracked in the
  // shared search-panel store (useSearchPanel.ts), so a slow result from
  // any surface can't land on top of a newer search from another.
  const [submitted, setSubmitted] = useState("");
  const [searching, setSearching] = useState(false);
  const EMPTY: KnowledgeBaseSearchResults = { tools: [], faq: [], personas: [], blog: [], topResult: null };

  const submitSearch = useCallback((raw: string) => {
    const value = raw.trim();
    if (value.length < 2) return;
    const token = beginSearchPanelSearch(value);
    setSearching(true);
    // Lazy-loaded so the ~130KB search corpus only ever downloads for
    // someone who actually searches. The .catch() matters on its own: a
    // stale tab's chunk hash 404ing after a deploy would otherwise
    // reach main.tsx's global preload-error handler and hard-reload the
    // page, wiping whatever was in the input.
    import("@/lib/knowledgeBaseSearch")
      .then(async ({ searchKnowledgeBase }) => {
        const r = await searchKnowledgeBase(value);
        if (!finishSearchPanelSearch(token, value, r)) return; // a newer search superseded this one
        setResults(r);
        setSubmitted(value);
        setSearching(false);
      })
      .catch(() => {
        failSearchPanelSearch(token);
        setSearching(false);
        showUpdateBanner();
      });
  }, []);

  const handleSearch = useCallback((value: string) => {
    setQuery(value);
    if (value.trim().length < 2) {
      cancelSearchPanelSearch();
      setSearching(false);
      setSubmitted("");
      setResults({ tools: [], faq: [], personas: [], blog: [], topResult: null });
      updateSearchPanelQuery(value, { tools: [], faq: [], personas: [], blog: [], topResult: null });
    }
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
    e.preventDefault();
    const value = query.trim();
    if (value.length < 2) return;
    // Results already shown for exactly this text: Enter opens the best
    // match (what Enter did before search became submit-only); otherwise
    // Enter runs the search.
    if (value === submitted && !searching && results.topResult) {
      recordSearchClick(results.topResult.href, "enter", 0);
      navigate(results.topResult.href);
      setQuery("");
      setSubmitted("");
      setResults(EMPTY);
      return;
    }
    submitSearch(value);
  };

  const goToTool = (toolId: string) => {
    navigate(`/tool/${toolId}`);
    setQuery("");
    setResults({ tools: [], faq: [], personas: [], blog: [], topResult: null });
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          aria-label="Search tools"
          enterKeyHint="search"
          placeholder={`Search ${SITE_FACTS.toolCountLabel} tools... (press Enter)`}
          value={query}
          onChange={e => handleSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 200)}
          className="pl-9 pr-14 w-64 lg:w-80 h-9 bg-secondary/50 border-transparent focus:border-primary/30 focus:bg-background transition-all"
        />
        {/* Discoverability hint for the global command palette (Cmd/Ctrl+K,
            see CommandPalette.tsx) -- clicking it opens the palette
            directly, so mouse users find the same fast, keyboard-first
            search power-users get, without needing to already know the
            shortcut exists. This box's own click-to-focus dropdown search
            is left completely untouched — two real entry points to the
            same underlying search engine, not a replacement. */}
        <button
          type="button"
          onClick={() => document.dispatchEvent(new KeyboardEvent("keydown", { key: "k", ctrlKey: true }))}
          className="absolute right-2 top-1/2 -translate-y-1/2 hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-border/60 text-[10px] font-medium text-muted-foreground hover:border-primary/30 hover:text-foreground transition-colors"
          aria-label="Open command palette"
        >
          <span className="text-xs">⌘</span>K
        </button>
      </div>
      {/* Desktop-only docked side panel (SearchSidePanel, mounted once
          globally) takes over search-results presentation there instead
          -- this inline dropdown stays for mobile, where that panel
          renders hidden. */}
      {isMobile && isFocused && searching && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 rounded-md border bg-popover px-3 py-2 text-sm text-muted-foreground shadow-md">
          Searching...
        </div>
      )}
      {isMobile && isFocused && !searching && submitted.length > 1 && submitted === query.trim() && (
        <SearchResultsDropdown
          results={results}
          query={query}
          onSelectTool={goToTool}
        />
      )}
    </div>
  );
}

function Header() {
  const { user, isAuthenticated, logout } = useAuth();
  const { locale, localizedHref } = useLocale();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [location] = useLocation();
  // The homepage has its own big "What's on your mind?" search in the hero
  // -- showing this header search too would be a duplicate on that one
  // page. Every other page has no other search, so it stays there.
  const isHomepage = location === "/";

  return (
    <header className="sticky top-0 z-50 glass border-b border-border/50">
      <div className="container flex items-center justify-between h-16">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2 shrink-0">
          <LogoMark className="h-8 w-8" />
          <span className="font-bold text-sm sm:text-lg tracking-tight whitespace-nowrap">
            <span className="text-primary">F</span>reeDailyPro
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1">
          <CategoryDropdown />
          <ForYourWorkDropdown />
        </nav>

        {/* Search + Auth. Confirmed by measuring the live header at real
            phone widths, not assumed: at 360/390/412px the "Open menu"
            button sat at x=428 -- off the right edge of the screen --
            and the whole page scrolled sideways by 16-68px; it only fit
            at 430px and wider, i.e. on no common phone. Fixed by tighter
            gaps and a smaller logo on phones, and by dropping the
            header language switcher below `sm` (the mobile menu already
            contains one, so nothing becomes unreachable). */}
        <div className="flex items-center gap-1 sm:gap-3">
          {!isHomepage && (
            <div className="hidden sm:block">
              <SearchBar />
            </div>
          )}

          <ProStatusBadge />
          <CreateShortcutButton variant="full" />
          <div className="md:hidden max-[359px]:hidden">
            <CreateShortcutButton variant="icon" />
          </div>

          <ThemeToggle />

          <Link href="/contact" title="Contact" className="hidden sm:block">
            <Button variant="ghost" size="icon" aria-label="Contact">
              <Mail className="h-4 w-4" />
            </Button>
          </Link>
          <Link href={localizedHref("/blog")} title="Blog" className="hidden sm:block">
            <Button variant="ghost" size="icon" aria-label="Blog">
              <BookOpen className="h-4 w-4" />
            </Button>
          </Link>
          <Link href="/pricing" title="Pricing" className="hidden sm:block">
            <Button variant="ghost" size="icon" aria-label="Pricing">
              <Tag className="h-4 w-4" />
            </Button>
          </Link>
          <Link href="/my-toolkit" title="My Toolkit">
            <Button variant="ghost" size="icon" aria-label="My Toolkit">
              <Star className="h-4 w-4" />
            </Button>
          </Link>
          {/* Real, genuine reason to show this now: nav/footer/common UI
              strings actually have real translations across 10 locales as
              of tonight, unlike when this was originally hidden (every
              locale file was still 100% English then). Tool content itself
              is still English regardless of locale -- that's the honest
              boundary of what's translated right now, and the homepage
              trust badge (`availableInLanguages`) is worded to reflect
              exactly that, not overclaim full-site translation. */}
          <div className="hidden sm:block">
            <LanguageSwitcher />
          </div>

          {isAuthenticated ? (
            <Link href="/profile" title="Account">
              <Button variant="ghost" size="icon" aria-label="Account">
                <User className="h-4 w-4" />
              </Button>
            </Link>
          ) : (
            <Link href="/profile" title="Sign In">
              <Button variant="ghost" size="icon" aria-label="Sign In">
                <User className="h-4 w-4" />
              </Button>
            </Link>
          )}

          {/* Mobile menu toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-border/50 bg-background">
          <div className="container py-4 space-y-3">
            {!isHomepage && (
              <div className="sm:hidden">
                <SearchBar />
              </div>
            )}
            <div className="flex flex-col gap-1">
              <Link
                href="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 p-2 rounded-md hover:bg-accent transition-colors text-sm"
              >
                <Mail className="h-4 w-4" /> Contact
              </Link>
              <Link
                href={localizedHref("/blog")}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 p-2 rounded-md hover:bg-accent transition-colors text-sm"
              >
                <BookOpen className="h-4 w-4" /> Blog
              </Link>
              {/* Pricing was reachable on desktop (the icon row above is
                  hidden sm:block) but had no equivalent entry point on
                  mobile at all — not hidden-but-findable, genuinely
                  absent from this menu. Placed as its own bold row, not
                  folded in with Contact/Blog, since it's the one link
                  here meant to lead somewhere rather than just inform. */}
              <Link
                href="/pricing"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 p-2 rounded-md hover:bg-accent transition-colors text-sm font-semibold text-primary"
              >
                <Tag className="h-4 w-4" /> Pricing
              </Link>
              <div className="p-2">
                <LanguageSwitcher />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {visibleCategories.map(cat => (
                <Link
                  key={cat.id}
                  href={localizedHref(`/category/${cat.id}`)}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 p-2 rounded-md hover:bg-accent transition-colors"
                >
                  <cat.icon className="h-4 w-4" style={{ color: cat.color }} />
                  <span className="text-sm">{getCategoryName(locale, cat.id, cat.name)}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const { strings, locale, localizedHref } = useLocale();
  const [allToolsOpen, setAllToolsOpen] = useState(
    () => typeof window !== "undefined" && !!window.matchMedia?.("(min-width: 768px)").matches,
  );
  return (
    <footer className="border-t border-border/50 bg-muted/30 mt-auto">
      <div className="container py-6 md:py-8">
        <div className="grid grid-cols-2 md:grid-cols-[1.6fr_1fr_1fr_1fr] gap-x-6 gap-y-6 md:gap-8">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 mb-2">
              <LogoMark className="h-7 w-7" />
              <span className="font-bold text-base">
                <span className="text-primary">F</span>reeDailyPro
              </span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
              {strings.footer.tagline}
            </p>
          </div>
          <nav aria-label={strings.footer.company}>
            <h2 className="font-semibold text-sm mb-2">{strings.footer.company}</h2>
            <ul className="space-y-1.5 text-sm text-muted-foreground">
              <li><Link href="/about" className="hover:text-foreground transition-colors">{strings.footer.about}</Link></li>
              <li><Link href={localizedHref("/blog")} className="hover:text-foreground transition-colors">{strings.footer.blog}</Link></li>
              <li><Link href="/careers" className="hover:text-foreground transition-colors">Careers</Link></li>
              <li><Link href="/contact" className="hover:text-foreground transition-colors">{strings.footer.contact}</Link></li>
            </ul>
          </nav>
          <nav aria-label="Follow and pricing">
            <h2 className="font-semibold text-sm mb-2">Explore</h2>
            <ul className="space-y-1.5 text-sm text-muted-foreground">
              <li><Link href="/pricing" className="hover:text-foreground transition-colors">{strings.footer.pricing}</Link></li>
              <li><Link href="/newsletter" className="hover:text-foreground transition-colors">Newsletter</Link></li>
              <li><a href="https://x.com/FreeDailyPro" target="_blank" rel="noopener noreferrer me" className="hover:text-foreground transition-colors">X (Twitter)</a></li>
              <li><a href="https://www.linkedin.com/in/freedailypro/" target="_blank" rel="noopener noreferrer me" className="hover:text-foreground transition-colors">LinkedIn</a></li>
              <li><a href="https://www.youtube.com/@freedailypro" target="_blank" rel="noopener noreferrer me" className="hover:text-foreground transition-colors">YouTube</a></li>
              <li><a href="https://www.reddit.com/user/FreeDailyPro/" target="_blank" rel="noopener noreferrer me" aria-label="FreeDailyPro on Reddit" className="hover:text-foreground transition-colors">Reddit</a></li>
              <li><a href="https://dev.to/freedailypro" target="_blank" rel="noopener noreferrer me" aria-label="FreeDailyPro on DEV Community" className="hover:text-foreground transition-colors">DEV</a></li>
            </ul>
          </nav>
          <nav aria-label="Legal" className="col-span-2 md:col-span-1">
            <h2 className="font-semibold text-sm mb-2">Legal</h2>
            <ul className="flex flex-wrap gap-x-5 gap-y-1.5 md:block md:space-y-1.5 text-sm text-muted-foreground">
              <li><Link href="/privacy" className="hover:text-foreground transition-colors">{strings.footer.privacyPolicy}</Link></li>
              <li><Link href="/terms" className="hover:text-foreground transition-colors">{strings.footer.termsOfService}</Link></li>
              <li><button type="button" onClick={openConsentSettings} className="hover:text-foreground transition-colors">Cookie settings</button></li>
            </ul>
          </nav>
        </div>

        {/* Real badges/links for the marketplaces we're actually listed
            on -- Product Hunt's is their own official embed badge (post_id
            1229656, confirmed live and rendering correctly before adding
            here); SaaSHub doesn't offer a free embeddable badge (checked
            directly -- their "Feature My Product"/"Product Promotion" links
            both lead to paid featured placement, not a badge generator), so
            that one is a plain link to our real listing instead. */}
        <div className="mt-6 pt-5 border-t border-border/50 flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="text-sm font-semibold text-muted-foreground">Featured on</span>
          <a
            href="https://www.producthunt.com/products/freedailypro"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="FreeDailyPro on Product Hunt"
          >
            <img
              src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1229656&theme=light"
              alt={`FreeDailyPro - ${SITE_FACTS.toolCountPhrase} in your browser | Product Hunt`}
              width="200"
              height="43"
              loading="lazy"
            />
          </a>
          <a
            href="https://www.saashub.com/freedailypro"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4"
          >
            FreeDailyPro on SaaSHub
          </a>
        </div>

        {/* Full site index: every tool, listed as real text/links, grouped by
            category. Compact list form (category heading + comma-separated
            tools) rather than a card grid -- this is genuine navigation for
            both visitors scanning for a tool by name and for search engines,
            just kept visually distinct from the category cards shown
            elsewhere on the page rather than repeating that same layout. */}
        <details
          className="mt-4"
          open={allToolsOpen}
          onToggle={(e) => setAllToolsOpen((e.currentTarget as HTMLDetailsElement).open)}
        >
          {/* Collapsed on phones (it is ~3 screens of links), open on larger screens.
              The links stay in the page either way. */}
          <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between font-semibold text-sm mb-2 [&::-webkit-details-marker]:hidden">
            <h2>{strings.footer.allTools}</h2>
            <span aria-hidden className="text-muted-foreground">{allToolsOpen ? "−" : "+"}</span>
          </summary>
          <div className="space-y-3 mt-2">
            {/* Premium Applications get their own row here, matching every
                category row's exact visual pattern, since they're
                deliberately filtered OUT of each category's own row just
                below (see the FEATURED_APP_IDS filter) -- without this
                row they'd have silently dropped out of the site's full
                index entirely once that filter shipped, not just moved
                to their new hub page. */}
            {([["/premium-apps", "Premium Applications", FEATURED_APP_META.filter((a) => isPremiumApp(a.id))], ["/workflow-products", "Workflow Products", WORKFLOW_PRODUCT_IDS.map((id) => getToolById(id)).filter((t): t is NonNullable<typeof t> => !!t).map((t) => ({ id: t.id, name: t.name }))]] as const).map(([href, label, apps]) => (
              <div key={href}>
                <Link href={localizedHref(href)} className="text-[11px] font-semibold text-foreground/80 hover:text-primary transition-colors">{label}:</Link>{" "}
                <span className="text-[10px] leading-relaxed">
                  {apps.map((app, i) => (
                    <span key={app.id}>
                      <Link href={`/tool/${app.id}`} className="text-muted-foreground hover:text-foreground transition-colors">{app.name}</Link>
                      {i < apps.length - 1 ? ", " : ""}
                    </span>
                  ))}
                </span>
              </div>
            ))}
            <div>
              <Link href={localizedHref("/professional-tools")} className="text-[11px] font-semibold text-foreground/80 hover:text-primary transition-colors">Professional Tools</Link>{" "}
              <span className="text-[10px] text-muted-foreground">(every category below)</span>
            </div>
            <div>
              <Link href="/bundles" className="text-[11px] font-semibold text-foreground/80 hover:text-primary transition-colors">
                Bundles:
              </Link>{" "}
              <span className="text-[10px] leading-relaxed">
                {BUNDLES.map((b, i) => (
                  <span key={b.id}>
                    <Link href={`/bundle/${b.id}`} className="text-muted-foreground hover:text-foreground transition-colors">{b.title}</Link>
                    {i < BUNDLES.length - 1 ? ", " : ""}
                  </span>
                ))}
              </span>
            </div>
            {visibleCategories.map(category => {
              const footerTools = category.tools.filter(t => !FEATURED_APP_IDS.has(t.id));
              return (
              <div key={category.id}>
                <Link
                  href={localizedHref(`/category/${category.id}`)}
                  className="text-[11px] font-semibold text-foreground/80 hover:text-primary transition-colors"
                >
                  {getCategoryName(locale, category.id, category.name)}:
                </Link>{" "}
                <span className="text-[10px] leading-relaxed">
                  {footerTools.map((tool, i) => (
                    <span key={tool.id}>
                      <Link
                        href={`/tool/${tool.id}`}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {tool.name}
                      </Link>
                      {i < footerTools.length - 1 ? ", " : ""}
                    </span>
                  ))}
                </span>
              </div>
              );
            })}
          </div>
        </details>

        <div className="mt-6 pt-4 border-t border-border/50 text-center text-sm text-muted-foreground">
          &copy; {new Date().getFullYear()} FreeDailyPro. {strings.footer.allRightsReserved}
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }: { children: React.ReactNode }) {
  useAutoResumeCheckout();
  const searchPanel = useSearchPanelState();
  const isMobileForPanel = useIsMobile();
  const reserveForPanel = searchPanel.isOpen && !isMobileForPanel;

  return (
    <OfflineGate>
      <div className="min-h-screen flex flex-col">
        <Header />
        {/* Reserves real layout space equal to the search panel's width
            while it's open, rather than the panel just overlaying
            whatever page content happened to be underneath it (a real,
            confirmed bug -- the homepage's own "Used on this device"
            card was getting covered rather than the page making room).
            Guarded by the same mobile check the panel's own hidden
            md:flex class uses -- isOpen alone isn't enough, since
            someone can type a long-enough query on mobile too; without
            this guard the page would reserve space for a panel that
            never actually becomes visible there. */}
        <main className="flex-1 transition-[margin] duration-150 max-sm:pb-20" style={{ marginRight: reserveForPanel ? searchPanel.width : 0 }}>{children}</main>
        <Footer />
        <FeedbackWidget />
        <CommandPaletteTrigger />
        <DailyLimitPaywallDialog />
        <FunnelBeacon />
        <GuestCheckoutDialog />
        <PdfPasswordDialog />
        <SearchSidePanel />
      </div>
    </OfflineGate>
  );
}
